/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Extent } from 'ol/extent';
import React from 'react';
import { useSelector, useStore } from 'react-redux';
import { layerSelectors, mapActions, mapSelectors } from '@opengeoweb/store';
import type { CoreAppStore } from '@opengeoweb/store';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import {
  getLayerExtentForProjection,
  useGetWMLayerInstance,
  useViewFromLayer,
} from '@opengeoweb/webmap-react';
import { getStoredViewObjectForMap } from './useGetOpenLayersView';

interface InitialZoomToLayerConnectProps {
  mapId: string;
  onHomeExtentResolved: (extent: Extent) => void;
}

const InitialZoomToLayerConnect: React.FC<InitialZoomToLayerConnectProps> = ({
  mapId,
  onHomeExtentResolved,
}) => {
  const initialZoomToLayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getInitialZoomToLayerId(store, mapId),
  );
  const layer = useSelector((store: CoreAppStore) =>
    initialZoomToLayerId
      ? (layerSelectors.getLayerById(
          store,
          initialZoomToLayerId,
        ) as OpenLayersLayerProps)
      : undefined,
  );

  const store = useStore<CoreAppStore>();
  const view = useViewFromLayer(layer);
  const { wmLayer } = useGetWMLayerInstance(layer);
  const hasResolvedInitialLayerView = React.useRef(false);

  React.useEffect(() => {
    const storedView = getStoredViewObjectForMap(mapId, store);
    if (storedView.view !== view) {
      storedView.setView(view);
    }
  }, [mapId, store, view]);

  React.useEffect(() => {
    if (hasResolvedInitialLayerView.current || !wmLayer) {
      return;
    }

    const extent = getLayerExtentForProjection(
      wmLayer,
      view.getProjection().getCode(),
    );

    if (extent) {
      onHomeExtentResolved([...extent]);
    }

    hasResolvedInitialLayerView.current = true;
    store.dispatch(
      mapActions.setPendingInitialZoomToLayer({
        mapId,
        layerId: undefined,
      }),
    );
  }, [mapId, onHomeExtentResolved, store, view, wmLayer]);

  return null;
};

export default InitialZoomToLayerConnect;
